import { Typography } from "antd";
import dayjs from "dayjs";
import { useState } from "react";
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, TooltipProps, XAxis, YAxis } from "recharts";
import { NameType, ValueType } from "recharts/types/component/DefaultTooltipContent";
import { formatCurrencyShort, formatMoney, toTitleCase } from "utils/functions";

const COLORS = ["#6366f1", "#14b8a6"];

export default function StackedHoverLineChart<K extends string>({
  data,
  valueKeys,
  labelKey,
}: {
  data: ({ [key in K]: number } & { date: string })[];
  valueKeys: K[];
  labelKey: "date";
}) {
  const [hoveredX, setHoveredX] = useState<string | null>(null);

  const CustomTooltip = ({ active, payload, label }: TooltipProps<ValueType, NameType>) => {
    if (active && payload && payload.length) {
      setHoveredX(label as string);
      return (
        // ######### Default Recharts Tooltip
        // <div style={{ background: "white", border: "1px solid #ccc", padding: 10 }}>
        //   <strong>{label}</strong>
        //   {payload.map((entry, index) => (
        //     <div key={index} style={{ color: entry.color }}>
        //       {entry.name}: {entry.value}
        //     </div>
        //   ))}
        // </div>
        <div className="p-2 bg-white shadow shadow-black/10 rounded border border-solid border-gray-200 flex flex-col gap-1">
          <p className="font-medium text-gray-800 text-sm">
            {dayjs(payload[0].payload[labelKey], "YYYY-MM-DD").format("MMMM DD, YYYY")}
          </p>
          {valueKeys?.map((vk, i) => {
            return (
              <div className="inline-flex gap-1 items-center" key={vk}>
                <div style={{ background: COLORS[i] }} className="w-1 h-4 rounded-md" />
                <Typography.Text type="secondary" style={{ fontSize: 13 }} key={vk}>
                  {toTitleCase(vk, { sep: "_" })}:{" "}
                </Typography.Text>
                <span className="text-gray-800 font-bold text-sm" style={{ fontVariantNumeric: "tabular-nums" }}>
                  {formatMoney(payload?.[0].payload?.[vk] as any)}
                </span>
              </div>
            );
          })}
        </div>
      );
    }
    setHoveredX(null);
    return null;
  };

  // const seriesKeys = data?.length > 0 ? Object.keys(data?.[0]).filter((el) => el !== "x") : [];
  const allKeyValues = data.map((el) => valueKeys.map((vk) => el[vk])).flat();
  // const uniqueMonthYears = Array.from(new Set(data.map((el) => dayjs(el[labelKey], "YYYY-MM-DD").format("MMM YY"))));

  return (
    <ResponsiveContainer width="100%" height={"100%"}>
      <LineChart data={data} margin={{ top: 20, right: 40, left: 10, bottom: 20 }}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis
          dataKey={labelKey}
          ticks={getMonthlyTicks(data)}
          tickFormatter={(val) => dayjs(val, "YYYY-MM-dd").format("MMM YYYY")}
        />
        <YAxis
          type="number"
          // domain={["dataMin", "dataMax"]}
          domain={[Math.min(...allKeyValues), Math.max(...allKeyValues)]}
          tickFormatter={(val) => formatCurrencyShort(val)}
          allowDataOverflow={true}
        />
        <Tooltip content={<CustomTooltip />} />
        {valueKeys.map((key, index) => (
          <Line
            key={key}
            type="monotone"
            dataKey={key}
            stroke={COLORS[index % COLORS.length]}
            strokeWidth={2}
            dot={({ cx, cy, payload }) =>
              payload[labelKey] === hoveredX ? (
                <circle cx={cx} cy={cy} r={5} fill={COLORS[index % COLORS.length]} stroke="#fff" strokeWidth={1.5} />
              ) : (
                <></>
              )
            }
            isAnimationActive={false}
            // stackId="1"
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
}

const getMonthlyTicks = (data: { date: string }[]) => {
  const seenMonths = new Set();
  return data
    .map((d) => {
      const monthKey = dayjs(d.date, "YYYY-MM-dd").format("MMM YY");
      if (!seenMonths.has(monthKey)) {
        seenMonths.add(monthKey);
        return d.date;
      }
      return null;
    })
    .filter(Boolean) as string[];
};
